/**
 * Opt-in trip alerts. Never prompts for permission on load.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router";
import { TRAVELLER_PROFILES, defaultPreferences, type DevicePreferences, type PushDevice, type SetupStatus } from "@shared/push.ts";
import {
  confirmTestPush,
  detectSetupStatus,
  dismissSetupLocally,
  effectiveSetupStatus,
  enablePushSubscription,
  fetchPushConfig,
  fetchThisDevice,
  isIos,
  isStandalone,
  saveDeviceRegistration,
  sendTestPush,
  setupDismissedLocally,
  statusCopy,
  type PushConfig,
} from "../../lib/push.ts";

function SetupForm({
  compact,
  onDone,
}: {
  compact?: boolean;
  onDone?: () => void;
}) {
  const [config, setConfig] = useState<PushConfig | null>(null);
  const [device, setDevice] = useState<PushDevice | null>(null);
  const [step, setStep] = useState(0);
  const [profileId, setProfileId] = useState("travelling-chris");
  const [preset, setPreset] = useState<"normal" | "essential">("normal");
  const [briefingTime, setBriefingTime] = useState("07:30");
  const [quietStart, setQuietStart] = useState("21:30");
  const [quietEnd, setQuietEnd] = useState("07:00");
  const [urgentBypass, setUrgentBypass] = useState(true);
  const [note, setNote] = useState<string | null>(null);
  const [noteKind, setNoteKind] = useState<"info" | "warn">("info");
  const [busy, setBusy] = useState(false);
  const detected = detectSetupStatus();

  useEffect(() => {
    void fetchPushConfig().then(setConfig).catch(() => setConfig(null));
    void fetchThisDevice().then((row) => {
      setDevice(row);
      if (row?.profileId) setProfileId(row.profileId);
      if (row?.preferences.preset) setPreset(row.preferences.preset);
      if (row?.preferences.briefingTime) setBriefingTime(row.preferences.briefingTime);
      if (row?.preferences.quietHoursStart) setQuietStart(row.preferences.quietHoursStart);
      if (row?.preferences.quietHoursEnd) setQuietEnd(row.preferences.quietHoursEnd);
      if (typeof row?.preferences.urgentBypassQuietHours === "boolean") setUrgentBypass(row.preferences.urgentBypassQuietHours);
    }).catch(() => setDevice(null));
  }, []);

  const status: SetupStatus = effectiveSetupStatus(device?.setupStatus, detected);
  const copy = statusCopy(status);
  const prefs: DevicePreferences = {
    ...defaultPreferences(preset),
    briefingTime,
    preset,
    quietHoursStart: quietStart,
    quietHoursEnd: quietEnd,
    urgentBypassQuietHours: urgentBypass,
  };
  const householdId = TRAVELLER_PROFILES.find((p) => p.id === profileId)?.householdId ?? "chris";

  function applyTestResult(test: Record<string, unknown>) {
    const failed = test.ok === false;
    setNoteKind(failed ? "warn" : "info");
    setNote(String(test.notice ?? (failed ? "The push service rejected the test." : "Test sent. Confirm if you saw it.")));
    void fetchThisDevice().then(setDevice).catch(() => undefined);
    return !failed;
  }

  async function persist(extra?: { subscription?: PushSubscriptionJSON; dismissedSetup?: boolean }) {
    const data = await saveDeviceRegistration({
      subscription: extra?.subscription,
      profileId,
      householdId,
      preferences: prefs,
      dismissedSetup: extra?.dismissedSetup,
      installedPwa: isStandalone(),
    });
    setDevice((data.device as PushDevice) ?? null);
    return data;
  }

  return (
    <section className={`card alert-setup ${compact ? "compact" : ""}`}>
      <p className="kicker">This phone</p>
      <h2 className="serif">{compact ? "Set up trip alerts" : "Trip alerts on this phone"}</h2>
      <p>{copy.detail}</p>
      {note ? <p className={`banner ${noteKind === "warn" ? "warn" : ""}`}>{note}</p> : null}
      {device?.lastPushError ? (
        <p className="banner warn">Last push service response: {device.lastPushError}</p>
      ) : null}

      {step === 0 || !compact ? (
        <>
          <label className="field">Whose phone is this?
            <select value={profileId} onChange={(e) => setProfileId(e.target.value)}>
              {TRAVELLER_PROFILES.map((row) => (
                <option key={row.id} value={row.id}>{row.label}</option>
              ))}
            </select>
          </label>
          <p className="muted">A profile only personalises alerts. Everyone with the family password can still read the whole trip.</p>
          <div className="chip-row">
            <button className={`chip ${preset === "normal" ? "selected" : ""}`} type="button" onClick={() => setPreset("normal")}>Normal alerts</button>
            <button className={`chip ${preset === "essential" ? "selected" : ""}`} type="button" onClick={() => setPreset("essential")}>Essential only</button>
          </div>
          <label className="field">Morning briefing
            <input type="time" value={briefingTime} onChange={(e) => setBriefingTime(e.target.value)} />
          </label>
        </>
      ) : null}

      {!compact ? (
        <>
          <label className="field">Quiet hours start
            <input type="time" value={quietStart} onChange={(e) => setQuietStart(e.target.value)} />
          </label>
          <label className="field">Quiet hours end
            <input type="time" value={quietEnd} onChange={(e) => setQuietEnd(e.target.value)} />
          </label>
          <label className="check-row">
            <input type="checkbox" checked={urgentBypass} onChange={(e) => setUrgentBypass(e.target.checked)} />
            Allow urgent travel updates during quiet hours
          </label>
          <p className="muted">Quiet hours are this app’s preference only. They cannot override Focus or Do Not Disturb. Sending a test ignores quiet hours.</p>
        </>
      ) : null}

      {detected === "needs_install" ? (
        <p className="banner warn">
          {isIos()
            ? "On iPhone, use Share → Add to Home Screen, then open Thailand, together from that icon before enabling notifications."
            : "Installing to the Home Screen is helpful on some Android browsers, but it is not universally required."}
        </p>
      ) : null}

      {detected === "blocked" ? (
        <p className="banner warn">Notifications were denied. Allow them for this site in system or browser settings, then return here. We cannot override Focus or Do Not Disturb.</p>
      ) : null}

      <div className="row-actions">
        {!compact ? (
          <button className="btn-secondary" type="button" disabled={busy} onClick={() => void persist().then(() => { setNoteKind("info"); setNote("Preferences saved on this phone."); })}>
            Save preferences
          </button>
        ) : null}
        {detected !== "unsupported" && detected !== "blocked" && detected !== "needs_install" ? (
          <button
            className="btn"
            type="button"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setNote(null);
              void (async () => {
                try {
                  await persist();
                  if (!config?.vapidPublicKey || !config.pushEnabled) {
                    setNoteKind("warn");
                    setNote("This phone is saved. Web Push is not configured on the server yet, so alerts stay in Updates.");
                    return;
                  }
                  const permission = await Notification.requestPermission();
                  if (permission !== "granted") {
                    setNoteKind("warn");
                    setNote("Permission was not granted. You can still use the Updates inbox.");
                    return;
                  }
                  const sub = await enablePushSubscription(config.vapidPublicKey);
                  await persist({ subscription: sub.toJSON() });
                  const test = await sendTestPush();
                  if (applyTestResult(test)) setStep(2);
                } catch (error) {
                  setNoteKind("warn");
                  setNote(error instanceof Error ? error.message : "Could not enable notifications.");
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            {busy ? "Working…" : "Enable notifications"}
          </button>
        ) : (
          <button className="btn-secondary" type="button" onClick={() => void persist()}>Save this phone</button>
        )}
        {status === "test_sent" || status === "subscription_active" ? (
          <button
            className="btn-secondary"
            type="button"
            onClick={() => {
              void confirmTestPush().then((data) => {
                setDevice((data.device as PushDevice) ?? null);
                setNoteKind("info");
                setNote(String(data.notice ?? "Test confirmed on this phone."));
                onDone?.();
              }).catch((error: Error) => {
                setNoteKind("warn");
                setNote(error.message);
              });
            }}
          >
            I saw the test
          </button>
        ) : null}
        {status === "test_sent" || status === "test_confirmed" || status === "subscription_active" ? (
          <button
            className="btn-ghost"
            type="button"
            onClick={() => {
              void sendTestPush()
                .then((data) => applyTestResult(data))
                .catch((error: Error) => {
                  setNoteKind("warn");
                  setNote(error.message);
                });
            }}
          >
            Send another test
          </button>
        ) : null}
      </div>
      <p className="muted">Lock-screen text stays short. Amounts and booking references stay inside the app. Native airline alerts remain useful backups.</p>
    </section>
  );
}

export function AlertSetupCard({ onDismiss }: { onDismiss?: () => void }) {
  const [hidden, setHidden] = useState(setupDismissedLocally());
  const [device, setDevice] = useState<PushDevice | null>(null);
  useEffect(() => {
    void fetchThisDevice().then(setDevice).catch(() => setDevice(null));
  }, []);
  if (hidden || (device?.setupStatus === "test_confirmed" && !["blocked", "permission_not_requested", "needs_install"].includes(detectSetupStatus())) || device?.dismissedSetup) return null;
  return (
    <div className="alert-setup-card">
      <SetupForm compact onDone={() => { setHidden(true); onDismiss?.(); }} />
      <button
        className="btn-ghost"
        type="button"
        onClick={() => {
          dismissSetupLocally();
          void saveDeviceRegistration({ dismissedSetup: true }).catch(() => undefined);
          setHidden(true);
          onDismiss?.();
        }}
      >
        Not now
      </button>
      <p><Link to="/essentials/phone">Permanent controls are under This phone</Link></p>
    </div>
  );
}

export function AlertSetupPanel() {
  return <SetupForm />;
}
